import {Alert, Box, Button, Link, Typography} from '@mui/material';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {Link as RouterLink} from 'react-router-dom';
import {Amenity} from 'src/apis/autoGenerate/types/amenity';
import {IntercomDevice} from 'src/apis/autoGenerate/types/intercomDevice';
import {intercomDeviceServiceFind} from 'src/apis/autoGenerate/services';
import IntercomDeviceListGrid from 'src/components/IntercomDeviceListGrid';
import message from 'src/components/Message';
import {useCanEdit} from 'src/hooks';

const DEVICES_ACCESS_PATH = '/devices';

export type AmenityLinkedDevicesPanelProps = {
  active: boolean;
  orgId: string;
  apartmentId: string;
  amenityDetail: Amenity | null;
  refetchAmenityDetail?: () => Promise<void>;
};

export default function AmenityLinkedDevicesPanel({
  active,
  orgId,
  apartmentId,
  amenityDetail,
  refetchAmenityDetail,
}: AmenityLinkedDevicesPanelProps) {
  const canEdit = useCanEdit();
  const [rows, setRows] = useState<IntercomDevice[]>([]);
  const [loading, setLoading] = useState(false);

  const linkedIdsKey = useMemo(() => {
    const ids =
      amenityDetail?.intercomDevices?.map(d => d.id).filter((id): id is string => Boolean(id)) || [];
    return [...ids].sort().join('|');
  }, [amenityDetail?.intercomDevices]);

  const fetchLinked = useCallback(async () => {
    const ids = linkedIdsKey ? linkedIdsKey.split('|').filter(Boolean) : [];
    if (!orgId || !apartmentId || ids.length === 0) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const res = await intercomDeviceServiceFind(orgId, ids, undefined, [apartmentId]);
      const list = res.data?.intercomDevices || [];
      const idSet = new Set(ids);
      setRows(list.filter(d => d.id && idSet.has(d.id)));
    } catch {
      setRows([]);
      message.error('Failed to load linked devices');
    } finally {
      setLoading(false);
    }
  }, [orgId, apartmentId, linkedIdsKey]);

  useEffect(() => {
    if (!active) {
      return;
    }
    fetchLinked();
  }, [active, fetchLinked]);

  const handleGridRefresh = useCallback(() => {
    void refetchAmenityDetail?.();
    void fetchLinked();
  }, [refetchAmenityDetail, fetchLinked]);

  if (!active) {
    return null;
  }

  const showEmptyGuidance = !loading && rows.length === 0;

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, minWidth: 0}}>
      <Typography variant="body2" color="text.secondary" component="div" sx={{mb: 2}}>
        Devices linked to this amenity appear in the table below, where you can manage call recipients
        and device settings. To add devices or link them to this amenity, open{' '}
        <Link component={RouterLink} to={DEVICES_ACCESS_PATH} underline="hover">
          Devices & Access
        </Link>
        .
      </Typography>

      {showEmptyGuidance && (
        <Alert severity="warning" sx={{mb: 2}}>
          <Typography variant="body2" sx={{mb: 1.5}}>
            This amenity is not currently linked to a device, so door access may not work during the
            reserved time slot.
          </Typography>
          <Button variant="contained" size="small" component={RouterLink} to={DEVICES_ACCESS_PATH}>
            Add Device
          </Button>
        </Alert>
      )}

      <IntercomDeviceListGrid
        rows={rows}
        loading={loading}
        orgId={orgId}
        canEdit={canEdit}
        onRefresh={handleGridRefresh}
        checkboxSelection
        emptyMessage={showEmptyGuidance ? '\u00a0' : 'No devices linked to this amenity'}
        slots={
          showEmptyGuidance
            ? {
                noRowsOverlay: () => <Box sx={{minHeight: 48}} />,
              }
            : undefined
        }
      />
    </Box>
  );
}
